import { IS_AI_AND_BRAND_KIT_ENABLED } from 'admin/constants/release-features';
import React, { useMemo } from 'react';
import cn from 'classnames';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { validateStory, type StoryIssue, type StoryIssueElement } from 'common/building-blocks';
import { generatePath } from 'common/utils/route';
import { STORY_CARD_EDITOR_PAGE } from 'admin/constants/routes';
import Tag from 'admin/components/common/Tag';
import { useAdminSelector } from 'admin/reducers';
import { selectEditableStory } from 'admin/reducers/story-editor/selectors';
import { selectCurrentVersion } from 'admin/reducers/version-reducer';
import {
	selectEditableCard,
	selectEditableCardId,
	selectEditableStateInfo,
	selectEditableStoryElements,
	selectEditableStorySettings,
	selectEditableSymbols,
} from 'admin/reducers/card-editor/selectors';
import { getMergeOfEditableStoryWithCardEditor } from 'admin/actions/card-editor/card-editor-sync';
import CardEditorIframeTransmitter from 'admin/components/pages/Story/CardEditor/IframeTransmitter';
import * as clipboard from 'admin/utils/clipboard';
import { useStoryEditorChatState } from 'admin/components/pages/Story/StoryEditorChat/hooks/use-story-editor-chat-state';
import { Icon } from 'admin/components/common/Icon';
import Text from 'admin/components/common/Text';
import { getStoryIssuePresentation } from './presentation';
import css from './StoryIssues.scss';

export function getStoryIssuePrompt(issue: StoryIssue): string {
	if (issue.code === 'leaf-contains-elements' && issue.repairContext) {
		const { repairContext } = issue;
		const formatElement = (element: StoryIssueElement) => {
			const id =
				repairContext.ownerKind === 'symbol-master' && repairContext.symbolMasterId
					? element.elementId === repairContext.symbolMasterId
						? repairContext.symbolMasterId
						: `${repairContext.symbolMasterId}.${element.elementId}`
					: element.elementId;
			return `${element.name} (${element.type}): ${id}`;
		};
		const owner = formatElement({
			elementId: issue.primaryElementId,
			nodeId: issue.primaryNodeId,
			name: issue.primaryElementName,
			type: issue.primaryElementType,
		});

		return [
			'Fix this Storycards structural issue:',
			issue.message,
			`Code: ${issue.code}`,
			`Owner: ${owner}`,
			...(issue.primaryNodeId ? [`Visible node: ${issue.primaryNodeId}`] : []),
			'Ancestor chain:',
			...(repairContext.ancestors.length > 0
				? repairContext.ancestors.map(element => `- ${formatElement(element)}`)
				: ['- Root']),
			'Misplaced subtree roots:',
			...repairContext.misplacedChildren.map(element => `- ${formatElement(element)}`),
			'',
			'Re-read the live owner, parent, and siblings with editor_script before changing anything.',
			'Determine a valid parent and the correct before/after position from the surrounding structure.',
			'If placement is ambiguous, ask the user.',
			'Move each subtree intact. Do not delete or flatten any subtree.',
		].join('\n');
	}

	return [
		'Fix this Storycards validation issue:',
		issue.message,
		`Code: ${issue.code}`,
		...(issue.location === 'card' ? [`Card: ${issue.cardId}`] : []),
		`Element: ${issue.primaryElementId}`,
		...(issue.primaryNodeId ? [`Node: ${issue.primaryNodeId}`] : []),
	].join('\n');
}

export function useStoryIssues(): StoryIssue[] {
	const story = useAdminSelector(selectEditableStory);
	const version = useAdminSelector(selectCurrentVersion);
	const data = useAdminSelector(selectEditableCard);
	const storySettings = useAdminSelector(selectEditableStorySettings);
	const storyElements = useAdminSelector(selectEditableStoryElements);
	const symbols = useAdminSelector(selectEditableSymbols);
	const validationInput = useMemo(
		() => ({ story, version, cardEditor: { data, storySettings, storyElements, symbols } }),
		[story, version, data, storySettings, storyElements, symbols]
	);
	return useMemo(() => {
		const { story: currentStory, version: currentVersion, cardEditor } = validationInput;
		if (!currentStory?.storyVersions[currentVersion]) return [];
		const snapshot = getMergeOfEditableStoryWithCardEditor(currentStory, cardEditor, currentVersion);
		return validateStory(snapshot.storyVersions[currentVersion].data);
	}, [validationInput]);
}

type StoryIssueGroup = {
	key: string;
	location: 'card' | 'story';
	cardId?: string;
	cardName: string;
	issues: StoryIssue[];
};

export function groupStoryIssues(
	issues: StoryIssue[],
	cardOrder: string[] = [],
	currentCardId?: string
): StoryIssueGroup[] {
	const groupsByCard = new Map<string, StoryIssueGroup>();
	const storyIssues: StoryIssue[] = [];

	issues.forEach(issue => {
		if (issue.location === 'story') {
			storyIssues.push(issue);
			return;
		}
		const group = groupsByCard.get(issue.cardId);
		if (group) {
			group.issues.push(issue);
			return;
		}
		groupsByCard.set(issue.cardId, {
			key: issue.cardId,
			location: 'card',
			cardId: issue.cardId,
			cardName: issue.cardName,
			issues: [issue],
		});
	});

	const groups = cardOrder.flatMap(cardId => {
		const group = groupsByCard.get(cardId);
		if (!group) return [];
		groupsByCard.delete(cardId);
		return [group];
	});
	groups.push(...groupsByCard.values());

	const currentIndex = currentCardId ? groups.findIndex(group => group.cardId === currentCardId) : -1;
	if (currentIndex > 0) groups.unshift(...groups.splice(currentIndex, 1));
	if (storyIssues.length > 0) {
		groups.splice(currentCardId && groups[0]?.cardId === currentCardId ? 1 : 0, 0, {
			key: 'story-structure',
			location: 'story',
			cardName: 'Story structure',
			issues: storyIssues,
		});
	}

	return groups;
}

export const StoryIssueList = ({
	issues,
	cardOrder,
	currentCardId,
	onSelect,
	onFix,
}: {
	issues: StoryIssue[];
	cardOrder?: string[];
	currentCardId?: string;
	onSelect?: (issue: StoryIssue) => void;
	onFix?: (issue: StoryIssue) => void;
}) => {
	if (issues.length === 0) {
		return (
			<Text size="label" className={css.empty}>
				No issues found.
			</Text>
		);
	}

	const groups = groupStoryIssues(issues, cardOrder, currentCardId);

	return (
		<div className={css.groups}>
			{groups.map((group, groupIndex) => {
				const isCurrent = group.location === 'card' && group.cardId === currentCardId;
				const headingId = `story-issues-group-${groupIndex}`;
				return (
					<section
						key={group.key}
						className={cn(css.group, { [css.currentGroup]: isCurrent })}
						aria-labelledby={headingId}
					>
						<header className={css.groupHeader}>
							<h3 id={headingId} className={css.groupTitle} title={group.cardName}>
								{group.cardName}
							</h3>
							<div className={css.groupMeta}>
								{isCurrent && <Tag color="brand">Current card</Tag>}
								<span className={css.issueCount}>
									{group.issues.length} {group.issues.length === 1 ? 'issue' : 'issues'}
								</span>
							</div>
						</header>
						<ul className={css.list} aria-labelledby={headingId}>
							{group.issues.map(issue => {
								const presentation = getStoryIssuePresentation(issue);
								return (
									<li
										className={css.item}
										key={`${issue.code}-${issue.location === 'card' ? issue.cardId : 'story'}-${issue.primaryElementId}-${issue.relatedElementIds?.join('-') ?? ''}`}
									>
										<button type="button" className={css.issue} onClick={() => onSelect?.(issue)}>
											<span className={css.issueMeta}>
												<span className={css.severity} data-severity={issue.severity}>
													{presentation.severityLabel}
												</span>
											</span>
											<strong className={css.issueTitle}>{presentation.title}</strong>
											<span className={css.issueDescription}>{presentation.description}</span>
											<span className={css.openHint}>
												{(issue.location === 'card' && issue.cardId === currentCardId) ||
												(issue.location === 'story' && issue.inspectionCardId === currentCardId)
													? 'Select element'
													: issue.location === 'card'
														? `Open on ${issue.cardName}`
														: issue.inspectionCardName
															? `Open on ${issue.inspectionCardName}`
															: 'Review structure'}
											</span>
										</button>
										{IS_AI_AND_BRAND_KIT_ENABLED && (
											<div className={css.actions}>
												<button
													type="button"
													onClick={() =>
														void clipboard.copy({ text: getStoryIssuePrompt(issue) })
													}
												>
													Copy for chat
												</button>
												{onFix && (
													<button type="button" onClick={() => onFix(issue)}>
														Fix with AI
													</button>
												)}
											</div>
										)}
									</li>
								);
							})}
						</ul>
					</section>
				);
			})}
		</div>
	);
};

const StoryIssues = ({ open, close }: { open: boolean; close: () => void }) => {
	const history = useHistory();
	const cardEditorRoute = useRouteMatch({ path: STORY_CARD_EDITOR_PAGE, exact: true });
	const currentCardId = useAdminSelector(selectEditableCardId);
	const editableState = useAdminSelector(selectEditableStateInfo);
	const story = useAdminSelector(selectEditableStory)!;
	const version = useAdminSelector(selectCurrentVersion);
	const issues = useStoryIssues();
	const { openWithDraft } = useStoryEditorChatState();
	const cardOrder = useMemo(
		() => story.storyVersions[version]?.data.steps.flatMap(step => step.cards.map(card => card._id)) ?? [],
		[story, version]
	);

	const selectIssue = (issue: StoryIssue) => {
		const targetCardId = issue.location === 'card' ? issue.cardId : issue.inspectionCardId;
		if (!targetCardId) return;
		close();
		const canSelectCurrent =
			cardEditorRoute &&
			issue.primaryNodeId &&
			(issue.location === 'card'
				? issue.cardId === currentCardId
				: issue.repairContext?.ownerKind === 'element' || issue.inspectionCardId === currentCardId);
		if (canSelectCurrent) {
			CardEditorIframeTransmitter.initiateElementSelectInClient({
				id: issue.primaryNodeId!,
				editableState,
				eventOptions: {
					metaKey: true,
					ctrlKey: true,
				},
			});
			return;
		}

		history.push(generatePath(STORY_CARD_EDITOR_PAGE, { storyId: story.id, cardId: targetCardId }));
	};

	return (
		<aside className={cn(css.panel, { [css.open]: open })} aria-label="Story issues">
			<div className={css.panelBody}>
				<div className={css.panelContainer}>
					<header className={css.header}>
						<Text size="subheading" weight="bold">
							Story issues
						</Text>
						<div className={css.headerActions}>
							<button type="button" className={css.close} aria-label="Close story issues" onClick={close}>
								<Icon type="close" width={16} color="currentColor" />
							</button>
						</div>
					</header>
					<StoryIssueList
						issues={issues}
						cardOrder={cardOrder}
						currentCardId={cardEditorRoute ? currentCardId : undefined}
						onSelect={selectIssue}
						onFix={issue => openWithDraft(getStoryIssuePrompt(issue))}
					/>
				</div>
			</div>
		</aside>
	);
};

export default StoryIssues;
